import * as React from "react";
import * as _ from "lodash";
import { Dispatch, connect } from "react-redux";
import {
    IApplicationState, AppActionCreators, ITicketingState, TicketingActionCreators, ITicketResponse,
    ITicketMessageResponse, ICenterState, ITicketLookupDataResponse, IContactListResponse, ContactActionCreators, ICannedResponseListResponse
} from "@nkr/store/index";
import { IUserListResponse, IBaseResponse, ITicketMessage, ISelectItem, IUser, ITicketMessageFile, ITicketCannedResponse } from "@nkr/models/index";
import { MessageType, TicketVisibility, TicketProperty, CannedResponseAvailability, UserGroup } from "@nkr/enums/index";
import { Button, FormControl, ButtonToolbar, OverlayTrigger, Popover } from "react-bootstrap";
import { toast } from "react-toastify";
import { Icon } from 'react-icons-kit';
import { mailReply } from 'react-icons-kit/fa/mailReply';
import { mailForward } from 'react-icons-kit/fa/mailForward';
import { stickyNote } from 'react-icons-kit/fa/stickyNote';
import Detail from "./detail";
import Message from "./message";
import Reply from "./reply";
import Note from "./note";
import Forward from "./forward";
import styled from "styled-components";
import ContactInfo from "./contact-info";
import Drawer from "rc-drawer";
import CannedResponses from "../canned-responses";

const UpdateButtonWrapper = styled(Button)`
    background-color: #145A32;
    color: white;
`;

interface IProps {
    id: number
}

interface IStateProps {
    ticketing: ITicketingState,
    centers: ICenterState,
    user: IUser
}

interface IDispatchProps {
    showLoading: (message: string) => Promise<void>,
    hideLoading: () => void,
    getTicket: (ticketId: number) => Promise<ITicketResponse>,
    updateTicket: (property: TicketProperty, value: string) => void,
    updateTicketTags: (list: ISelectItem[]) => void,
    saveTicketProperties: () => Promise<IBaseResponse>,
    saveTicketMessage: (
        type: MessageType,
        from: string,
        to: ISelectItem,
        cc: ISelectItem[],
        bcc: ISelectItem[],
        subject: string,
        message: string,
        visibility: TicketVisibility,
        parentMessageId: number,
        files: File[]) => Promise<ITicketMessageResponse>,
    saveTicketForward: (
        from: string,
        to: ISelectItem[],
        cc: ISelectItem[],
        bcc: ISelectItem[],
        subject: string,
        message: string,
        visibility: TicketVisibility,
        parentMessageId: number,
        files: File[]) => Promise<ITicketMessageResponse>,
    saveTicketNote: (
        to: ISelectItem,
        message: string,
        visibility: TicketVisibility,
        parentMessageId: number,
        files: File[]) => Promise<ITicketMessageResponse>,
    getUserList: () => Promise<IUserListResponse>,
    getLookupData: () => Promise<ITicketLookupDataResponse>,
    updateAttachments: (file: File) => void,
    getContacts: (search: string) => Promise<IContactListResponse>,
    getCannedResponses: () => Promise<ICannedResponseListResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

interface IState {
    dirty: boolean
    message: {
        show: boolean,
        type: MessageType,
        file: ITicketMessageFile
    },
    forward: {
        show: boolean
    },
    sortBy: string,
    showCannedResponses: boolean,
    selectedCannedResponse: ITicketCannedResponse
}

class Index extends React.Component<TProps, IState> {
    readonly state: IState = {
        dirty: false,
        message: {
            show: false,
            type: MessageType.Reply,
            file: null
        },
        forward: {
            show: false
        },
        sortBy: "DA",
        showCannedResponses: false,
        selectedCannedResponse: null
    }

    private container: HTMLDivElement;

    componentWillMount = () => {
        document.title = 'NKR: View Ticket #' + this.props.id;
    }

    componentDidMount = () => {
        this.getData();
    }

    handleReturnToList = () => {
        window.location.href = "/ticketing/tickets";
    }

    handleAssignedUserChange = (userId: number) => {
        if (userId > 0) {
            this.props.updateTicket(TicketProperty.AssignedUser, userId.toString());

            this.setState({
                dirty: true
            });
        }
    }

    handleEntityTypeChange = (entityType: number) => {
        this.props.updateTicket(TicketProperty.EntityType, entityType.toString());

        this.setState({
            dirty: true
        });
    }

    handleCenterChange = (centerId: number) => {
        this.props.updateTicket(TicketProperty.Center, centerId.toString());

        this.setState({
            dirty: true
        });
    }

    handleTicketTypeChange = (ticketTypeId: number) => {
        this.props.updateTicket(TicketProperty.TicketType, ticketTypeId.toString());

        this.setState({
            dirty: true
        });
    }

    handleTierChange = (tier: number) => {
        this.props.updateTicket(TicketProperty.TierLevel, tier.toString());

        this.setState({
            dirty: true
        });
    }

    handleStatusChange = (status: number) => {
        this.props.updateTicket(TicketProperty.TicketStatus, status.toString());

        this.setState({
            dirty: true
        });
    }

    handleGroupChange = (group: number) => {
        this.props.updateTicket(TicketProperty.Group, group.toString());

        this.setState({
            dirty: true
        });
    }

    handleUrgencyChange = (urgency: number) => {
        this.props.updateTicket(TicketProperty.Urgency, urgency.toString());

        this.setState({
            dirty: true
        });
    }

    handleTagsChange = (list: ISelectItem[]) => {
        this.props.updateTicketTags(list);

        this.setState({
            dirty: true
        });
    }

    handleUpdateProperties = () => {
        this.props.showLoading("Saving ticket properties, one moment please...").then(() => {
            this.props.saveTicketProperties().then(response => {
                this.setState({
                    dirty: false
                }, () => {
                    this.props.hideLoading();

                    if (response.data.success) {
                        toast.success("Saved ticket properties");
                    } else {
                        toast.error("Could not save ticket properties");
                    }
                })
            });
        });
    }

    handleReply = () => {
        this.setState({
            message: {
                show: true,
                type: MessageType.Reply,
                file: null
            },
            forward: {
                show: false
            }
        });
    }

    handleAddNote = () => {
        this.setState({
            message: {
                show: true,
                type: MessageType.Note,
                file: null
            },
            forward: {
                show: false
            }
        });
    }

    handleForward = () => {
        this.setState({
            forward: {
                show: true
            },
            message: { ...this.state.message, show: false }
        });
    }

    handleSortBy = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        this.setState({
            sortBy: e.currentTarget.value
        });
    }

    handleAddSave = (from: string, to: ISelectItem, cc: ISelectItem[], bcc: ISelectItem[], subject: string, message: string, visibility: TicketVisibility, files: File[]) => {
        let sortedMessages = _.orderBy(this.props.ticketing.ticket.messages, (message: ITicketMessage) => {
            return message.id;
        }, ['desc']);

        let lastMessage = sortedMessages.length > 0 ? sortedMessages[0] : null;
        let parentMessageId = lastMessage ? lastMessage.id : 0;

        this.props.showLoading('Sending reply for message, one moment please...').then(() => {
            this.props.saveTicketMessage(this.state.message.type, from, to, cc, bcc, subject, message, visibility, parentMessageId, files).then(response => {
                if (response.data.success) {
                    toast.success("Sent reply successfully");

                    this.setState({
                        message: { ...this.state.message, show: false }
                    }, () => {
                        this.props.hideLoading();
                    });
                }
            });
        });
    }

    handleForwardSave = (from: string, to: ISelectItem[], cc: ISelectItem[], bcc: ISelectItem[], subject: string, message: string, visibility: TicketVisibility, files: File[]) => {
        let sortedMessages = _.orderBy(this.props.ticketing.ticket.messages, (message: ITicketMessage) => {
            return message.id;
        }, ['desc']);

        let lastMessage = sortedMessages.length > 0 ? sortedMessages[0] : null;
        let parentMessageId = lastMessage ? lastMessage.id : 0;

        this.props.showLoading('Forwarding message, one moment please...').then(() => {
            this.props.saveTicketForward(from, to, cc, bcc, subject, message, visibility, parentMessageId, files).then(response => {
                if (response.data.success) {
                    toast.success("Forward sent successfully");

                    this.setState({
                        forward: { ...this.state.forward, show: false }
                    }, () => {
                        this.props.hideLoading();
                    });
                }
            });
        });
    }

    handleNoteSave = (to: ISelectItem, message: string, visibility: TicketVisibility, files: File[]) => {
        let sortedMessages = _.orderBy(this.props.ticketing.ticket.messages, (message: ITicketMessage) => {
            return message.id;
        }, ['desc']);

        let lastMessage = sortedMessages.length > 0 ? sortedMessages[0] : null;
        let parentMessageId = lastMessage ? lastMessage.id : 0;

        this.props.showLoading('Saving note, one moment please...').then(() => {
            this.props.saveTicketNote(to, message, visibility, parentMessageId, files).then(response => {
                if (response.data.success) {
                    toast.success('Note saved successfully');

                    this.setState({
                        message: { ...this.state.message, show: false }
                    }, () => {
                        this.props.hideLoading();
                    });
                }
            });
        });
    }

    handleAddCancel = () => {
        this.setState({
            message: { ...this.state.message, show: false }
        });
    }

    handleForwardCancel = () => {
        this.setState({
            forward: {
                show: false
            }
        });
    }

    handleFilesChanged = (file: File) => {
        this.props.updateAttachments(file);

        toast.success('File uploaded successfully...');
    }

    handleShowCannedResponses = () => {
        this.setState({
            showCannedResponses: true,
            selectedCannedResponse: null
        });
    }

    handleCloseCannedResponses = () => {
        this.setState({
            showCannedResponses: false,
            selectedCannedResponse: null
        });
    }

    handleApplyCannedResponse = (id: number) => {
        let selectedCannedResponse = this.props.ticketing.cannedResponses.find(x => x.id == id);

        this.setState({
            showCannedResponses: false,
            selectedCannedResponse
        });
    }

    saveContainer = (container: HTMLDivElement) => {
        this.container = container;
    };

    getContainer = () => {
        return this.container;
    };

    getData = () => {
        this.props.showLoading("Loading ticket, one moment please...").then(() => {
            return Promise.all([
                this.props.getTicket(this.props.id),
                this.props.getUserList(),
                this.props.getLookupData(),
                this.props.getCannedResponses()
            ]).then(response => {
                this.props.hideLoading();
            });
        });
    }

    render = () => {
        const ticketId = this.props.ticketing.ticket ? this.props.ticketing.ticket.id : 0;
        const messages = this.props.ticketing.ticket ? this.props.ticketing.ticket.messages : [];

        const sortedMessages = _.orderBy(messages, (message: ITicketMessage) => {
            return new Date(message.createdDateTime);
        }, this.state.sortBy == "DA" ? ["asc"] : ["desc"]);

        const centers = this.props.centers.list.filter(x => x.isNkrParticipant && x.isDeleted == false);

        let initialSubject = '';

        if (this.state.message.show && this.state.message.type == MessageType.Reply) {
            let noNotes = this.props.ticketing.ticket.messages.filter(x => x.type != MessageType.Note);
            let sorted = _.orderBy(noNotes, ['createdDateTime', 'id'], ['desc', 'desc']);
            let latest = sorted[0];

            initialSubject = 'Re: ' + latest.subject;
        }

        const nkrContacts = _.orderBy(this.props.ticketing.lookupData.nkrContactList, (item) => {
            return item.firstName + ' ' + item.lastName;
        });

        const cannedResponses = this.props.ticketing.cannedResponses.filter(x => {
            return x.availability == CannedResponseAvailability.AllAgents || (x.availability == CannedResponseAvailability.Myself && x.ownerId == this.props.user.id);
        });

        return (
            <>
                <div ref={this.saveContainer} />
                <div className="container-fluid" style={{ height: '100%' }}>
                    <div className="row">
                        <div className="col-xs-12 col-lg-12" style={{ paddingLeft: 0, paddingRight: 0 }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <div>
                                    <h1 style={{ marginBottom: 20 }}>Ticket #{ticketId}</h1>
                                </div>
                                <div>
                                    <Button bsStyle="link" onClick={this.handleReturnToList}>Return to List</Button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-xs-12 col-lg-12" style={{ backgroundColor: '#f5f7f9', boxShadow: '0 2px 4px 0 rgba(24,50,71,.08)', borderBottom: '1px #cfd7df solid' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: 10 }}>
                                <div className="btn-toolbar">
                                    <div className="btn-group">
                                        <Button onClick={this.handleReply}><Icon icon={mailReply} /> Reply</Button>
                                    </div>
                                    <div className="btn-group">
                                        <Button onClick={this.handleAddNote}><Icon icon={stickyNote} /> Add Note</Button>
                                    </div>
                                    <div className="btn-group">
                                        <Button onClick={this.handleForward}><Icon icon={mailForward} /> Forward</Button>
                                    </div>
                                </div>
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    <div style={{ marginRight: 5 }}>Sort by:</div>
                                    <div>
                                        <FormControl
                                            componentClass="select"
                                            value={this.state.sortBy}
                                            onChange={this.handleSortBy}
                                        >
                                            <option value="DA">Date Ascending</option>
                                            <option value="DD">Date Descending</option>
                                        </FormControl>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row" style={{ backgroundColor: '#eee', height: '100%' }}>
                        <div style={{ display: 'flex', height: '100%' }}>
                            <div style={{ width: '60%', padding: 10 }}>
                                <div style={{ height: '100%', width: "99.5%", paddingLeft: 10, paddingRight: 10, borderRadius: 4, border: "1px solid #ddd", backgroundColor: 'white' }}>
                                    {sortedMessages.map((message, index) =>
                                        <Message
                                            key={index}
                                            isCenter={false}
                                            ticket={this.props.ticketing.ticket}
                                            ticketMessage={message}
                                        />
                                    )}

                                    {!this.state.forward.show && !this.state.message.show && (
                                        <ButtonToolbar style={{ backgroundColor: "#eee", padding: 5 }}>
                                            <Button bsStyle="link" onClick={this.handleReply}>Reply</Button>
                                            <Button bsStyle="link" onClick={this.handleAddNote}>Add Note</Button>
                                            <Button bsStyle="link" onClick={this.handleForward}>Forward</Button>
                                        </ButtonToolbar>
                                    )}

                                    {this.state.message.show && this.state.message.type == MessageType.Reply && (
                                        <Reply
                                            show={this.state.message.show}
                                            ticket={this.props.ticketing.ticket}
                                            user={this.props.user}
                                            messageType={this.state.message.type}
                                            initialSubject={initialSubject}
                                            attachment={this.state.message.file}
                                            cannedResponse={this.state.selectedCannedResponse}
                                            onGetContacts={this.props.getContacts}
                                            onShowCannedResponses={this.handleShowCannedResponses}
                                            onSave={this.handleAddSave}
                                            onCancel={this.handleAddCancel}
                                        />
                                    )}

                                    {this.state.message.show && this.state.message.type == MessageType.Note && (
                                        <Note
                                            show={this.state.message.show}
                                            ticket={this.props.ticketing.ticket}
                                            user={this.props.user}
                                            attachment={this.state.message.file}
                                            toList={nkrContacts}
                                            cannedResponse={this.state.selectedCannedResponse}
                                            onShowCannedResponses={this.handleShowCannedResponses}
                                            onSave={this.handleNoteSave}
                                            onCancel={this.handleAddCancel}
                                        />
                                    )}

                                    {this.state.forward.show && (
                                        <Forward
                                            show={this.state.forward.show}
                                            user={this.props.user}
                                            ticket={this.props.ticketing.ticket}
                                            cannedResponse={this.state.selectedCannedResponse}
                                            onGetContacts={this.props.getContacts}
                                            onShowCannedResponses={this.handleShowCannedResponses}
                                            onSave={this.handleForwardSave}
                                            onCancel={this.handleForwardCancel}
                                        />
                                    )}
                                </div>
                            </div>

                            <div style={{ width: '20%', padding: 0, height: '100%' }}>
                                <div style={{ borderRight: '5px solid #ddd' }}>
                                    <div style={{ padding: "0px 15px 15px 5px" }}>
                                        <Detail
                                            ticket={this.props.ticketing.ticket}
                                            userGroup={this.props.user.group}
                                            userList={this.props.ticketing.users}
                                            ticketEntityTypeList={this.props.ticketing.lookupData.ticketEntityTypeList}
                                            ticketTypeList={this.props.ticketing.lookupData.ticketTypeList}
                                            centerList={this.props.ticketing.lookupData.centerList}
                                            groupList={this.props.ticketing.lookupData.ticketGroupList}
                                            urgencyList={this.props.ticketing.lookupData.ticketUrgencyList}
                                            tagList={this.props.ticketing.lookupData.tagList.map(x => { return { value: x.id.toString(), label: x.name }; })}
                                            onAssignedUserChange={this.handleAssignedUserChange}
                                            onCenterChange={this.handleCenterChange}
                                            onTicketTypeChange={this.handleTicketTypeChange}
                                            onTierChange={this.handleTierChange}
                                            onStatusChange={this.handleStatusChange}
                                            onGroupChange={this.handleGroupChange}
                                            onUrgencyChange={this.handleUrgencyChange}
                                            onTagsChange={this.handleTagsChange}
                                        />
                                    </div>

                                    <div style={{ padding: 10 }}>
                                        <UpdateButtonWrapper bsStyle="success" block disabled={!this.state.dirty} onClick={this.handleUpdateProperties}>Update</UpdateButtonWrapper>
                                    </div>
                                </div>
                            </div>

                            <div style={{ width: '20%', padding: 0, backgroundColor: '#f5f7f9', borderBottom: '1px solid #ddd' }}>
                                <div style={{ padding: "0px 15px 15px 15px", minHeight: 350 }}>
                                    <ContactInfo ticket={this.props.ticketing.ticket} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <Drawer
                    width="20vw"
                    // handler={false}
                    placement="right"
                    getContainer={this.getContainer}
                    open={this.state.showCannedResponses}
                >
                    <CannedResponses
                        cannedResponses={cannedResponses}
                        onApply={this.handleApplyCannedResponse}
                        onClose={this.handleCloseCannedResponses}
                    />
                </Drawer>
            </>
        );
    }
}

const mapStateToProps = (state: IApplicationState): IStateProps => {
    return {
        ticketing: state.ticketing,
        centers: state.centers,
        user: state.app.user
    };
}

const mapDispatchToProps = (dispatch: Dispatch<any>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            return dispatch(AppActionCreators.hideLoading());
        },
        getTicket: (ticketId) => {
            return dispatch(TicketingActionCreators.getTicket(ticketId));
        },
        updateTicket: (property, value) => {
            dispatch(TicketingActionCreators.updateTicket(property, value));
        },
        updateTicketTags: (list) => {
            dispatch(TicketingActionCreators.updateTicketTags(list));
        },
        saveTicketProperties: () => {
            return dispatch(TicketingActionCreators.saveTicketProperties());
        },
        saveTicketMessage: (type, from, to, cc, bcc, subject, message, visibility, parentMessageId, files) => {
            return dispatch(TicketingActionCreators.saveTicketMessage(type, from, to, cc, bcc, subject, message, visibility, parentMessageId, files));
        },
        saveTicketForward: (from, to, cc, bcc, subject, message, visibility, parentMessageId, files) => {
            return dispatch(TicketingActionCreators.saveTicketForward(from, to, cc, bcc, subject, message, visibility, parentMessageId, files));
        },
        saveTicketNote: (to, message, visibility, parentMessageId, files) => {
            return dispatch(TicketingActionCreators.saveTicketNote(to, message, visibility, parentMessageId, files));
        },
        getUserList: () => {
            return dispatch(TicketingActionCreators.getUserList([UserGroup.Admin, UserGroup.NkdoUser]));
        },
        getLookupData: () => {
            return dispatch(TicketingActionCreators.getLookupData());
        },
        updateAttachments: (file) => {
            dispatch(TicketingActionCreators.updateAttachments(file));
        },
        getContacts: (search) => {
            return dispatch(ContactActionCreators.getContacts(search));
        },
        getCannedResponses: () => {
            return dispatch(TicketingActionCreators.getCannedResponses());
        }
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(Index);
